import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { BreadCrumb, BreadCrumbs } from '../../components/BreadCrumbs';
import { CardBody } from '../../components/CardBody';
import { CardFooter } from '../../components/CardFooter';
import { ProductPriceSummary } from '../../components/ProductPriceSummary';
import { ImageLoader } from '../../product_detail/components/ImageLoader';
import { assetPathFnState, environmentContextState, toViewPriceFnState } from '../../recoil/atoms';
import { CardHeader } from '../../components/CardHeader';
import { ProductControls } from '../../components/ProductControls';
import { ModelName, useProductAssets } from '../../hooks/useProductAssets';
import { sizeImage } from '../../util';
import { ProductPerks } from '../../components/ProductPerks';
import { SelectionType } from '../../enums/SelectionType.enum';
import { GuaranteePanel } from '../../components/GuaranteePanel';
import { CollapsePanelDesktop } from '../../components/CollapsePanelDesktop';
import { CompleteTheLook } from '../../components/CompleteTheLook';
import { SlideToggle } from '../../components/SlideToggle';
import { isDefined, uniqueFilterByProperty } from '../../sizing-tool/utils/array-helper';
import { shortSizeToLongSize } from '../../product_detail/components/size.enum';
import { ProductReviewsDesktop } from '../../components/ProductReviewsDesktop';
import { useProductSelection } from '../../hooks/useProductSelection';
import { DivButton } from '../../root/components/DivButton';
import { CardTitle } from '../../components/CardTitle';
import { CollectionCards } from '../../collection/components/CollectionCards';
import { DiscoverHoneylove } from '../../sections/DiscoverHoneylove';
import { WistiaPanel } from '../../components/WistiaPanel';
import { useOnScreen } from '../../hooks/useOnScreen';
import { WorldsBestShapingPanel } from '../../components/WorldsBestShapingPanel';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import { ViewProduct } from '../../models/ViewProduct.model';
import { freeShippingThresholdInDollars } from '../../discounts';
import { WorldsBestTitle } from '../../components/WorldsBestTitle';
import styles from './ProductDetailPageDesktop.module.scss';

interface Props {
  breadCrumbs?: BreadCrumb[];
}

const toggleStyle: CSSProperties = {
  justifyContent: 'flex-start',
  fontWeight: 600,
  letterSpacing: '1.5px',
  fontSize: '12px',
  padding: '10px 10px 8px 10px',
};

const imageStyle: CSSProperties = {
  width: '100%',
};

const productControlsBaseStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  padding: '20px 20px 0 20px',
  marginBottom: 5,
  marginLeft: '-20px',
  marginRight: '-20px',
  backgroundColor: 'white',
  zIndex: 2,
};

const stickyStyle: CSSProperties = {
  boxShadow: 'rgba(0, 0, 0, 0.12) 0px 6px 8px',
};

const CollapsePanels: React.FC<{ product: ViewProduct }> = ({ product }) => {
  const isGiftCard = product.handle === ProductHandle.EGiftCard;

  return (
    <>
      <CollapsePanelDesktop
        title={`${!isGiftCard ? 'GARMENT ' : ''}DESCRIPTION`}
        style={{ marginTop: 10 }}
        toggleStyle={toggleStyle}
        type={'internal-state'}
        initialState={'active'}
      >
        <span className={styles.panelText}>{product.coreFeatures}</span>
      </CollapsePanelDesktop>
      {product.fitRecommendation && (
        <CollapsePanelDesktop title="FIT RECOMMENDATIONS" toggleStyle={toggleStyle} type={'internal-state'}>
          <span className={styles.panelText}>{product.fitRecommendation}</span>
        </CollapsePanelDesktop>
      )}
      {product.fabricDetails?.length > 0 && (
        <CollapsePanelDesktop
          title="FABRIC DETAILS"
          style={{ borderBottom: '1px solid black', paddingBottom: 1 }}
          toggleStyle={toggleStyle}
          type={'internal-state'}
        >
          <div className={styles.fabricDetails}>
            <span className={styles.panelText}>
              <ul>
                {product.fabricDetails.map((fd, i) => {
                  return <li key={`fabric-${i}`}>{fd}</li>;
                })}
              </ul>
            </span>
          </div>
        </CollapsePanelDesktop>
      )}
    </>
  );
};

// eslint-disable-next-line max-lines-per-function
export const ProductDetailPageDesktop: React.FC<Props> = ({ breadCrumbs }) => {
  const { product, color, variant } = useProductSelection(SelectionType.ProductSection, '0');
  const reviewsRef = useRef<HTMLDivElement>(null);
  const controlsTopRef = useRef<HTMLDivElement>(null);
  const assetPathFn = useRecoilValue(assetPathFnState);
  const productAssets = useProductAssets(product);
  const [activeModel, setActiveModel] = useState<ModelName>(productAssets.modelPhotos?.[0]?.modelName);
  const toViewPriceFn = useRecoilValue(toViewPriceFnState);
  const { warehouseSaleSKUs, warehouseSaleEnabled } = useRecoilValue(environmentContextState);

  const controlsTopOnScreen = useOnScreen(controlsTopRef);
  const controlsAreSticky = controlsTopRef?.current && controlsTopOnScreen === false;
  const productControlsStyle = controlsAreSticky ? { ...productControlsBaseStyle, ...stickyStyle } : productControlsBaseStyle;
  const isGiftCard = product.handle === ProductHandle.EGiftCard;

  const wistiaVideoId = productAssets.videos[0]?.wistiaId;

  useEffect(() => {
    if (reviewsRef?.current && window.location.hash.match('#reviews')) {
      reviewsRef.current.scrollIntoView();
    }
  }, [reviewsRef]);
  useEffect(() => {
    setActiveModel(productAssets.modelPhotos?.[0]?.modelName);
  }, [productAssets]);

  const modelPhotos = useMemo(() => {
    return productAssets.modelPhotos.filter((p) => p.color === color && p.modelName === activeModel);
  }, [color, productAssets, activeModel]);

  const possibleModels = useMemo(() => {
    return productAssets.modelPhotos.filter((p) => p.color === color).filter(uniqueFilterByProperty((model) => model.modelName));
  }, [color, productAssets]);

  useEffect(() => {
    setActiveModel(possibleModels[0]?.modelName);
  }, [possibleModels]);

  const fabricPhotos = useMemo(() => {
    const photos = productAssets.fabricPhotos.filter((p) => p.color === color);
    return photos.length > 0 ? photos : [productAssets.fabricPhotos[0]].filter(isDefined);
  }, [color, productAssets]);

  const zoomInPhotos = useMemo(() => {
    const photos = productAssets.zoomInPhotos.filter((p) => p.color === color);
    return photos.length > 0 ? photos : [productAssets.zoomInPhotos[0]].filter(isDefined);
  }, [color, productAssets]);

  const topPhotos = product.handle === ProductHandle.EGiftCard ? productAssets.renderPhotos : [...modelPhotos, ...zoomInPhotos];
  const bottomPhotos = [...fabricPhotos, ...productAssets.productFeaturePhotos];

  const isEmbedded = !breadCrumbs;

  const isWarehouseSaleVariant = !!product.variants.find((v) => warehouseSaleEnabled && warehouseSaleSKUs.includes(v.sku) && v.color === color);
  const isNoReturns = !product.isReturnable || isWarehouseSaleVariant;

  const guarantees = [
    { message: `Free shipping over ${toViewPriceFn(freeShippingThresholdInDollars, 0)}`, usaOnly: true },
    isNoReturns ? undefined : { message: 'Free 30-day returns and exchanges', usaOnly: true },
  ].filter(isDefined);

  return (
    <>
      <CardBody style={{ paddingTop: isEmbedded ? 30 : 0 }}>
        <div className={styles.bodyContainer}>
          <div className={styles.leftWrapper}>
            {possibleModels.length >= 2 && (
              <div className={styles.textModelToggle}>
                <span>Select model size:</span>
                {possibleModels.map((model, i) => {
                  const isActive = activeModel === model.modelName;
                  return (
                    <React.Fragment key={model.modelName}>
                      <DivButton
                        disabled={isActive}
                        onClick={() => setActiveModel(model.modelName)}
                        style={isActive ? { color: '#6065e0' } : {}}
                        label="Choose model"
                      >
                        {`${model.modelName}, ${shortSizeToLongSize(model.size)}`}
                      </DivButton>
                      {i !== possibleModels.length - 1 && <span>|</span>}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
            <div className={topPhotos.length === 1 ? styles.singleImageTop : styles.top}>
              {topPhotos.map((p, i) => {
                const hasEvenNumber = topPhotos.length % 2 === 0;
                return hasEvenNumber || topPhotos.length === 1 || i < topPhotos.length - 1 ? (
                  <div key={`top-photo-${i}`} className={topPhotos.length === 1 ? styles.imageBoxSingle : styles.imageBox}>
                    <ImageLoader style={imageStyle} className={styles.grid} key={p.src} alt={`${product.title} ${i}`} src={sizeImage(p.src, '800x')} />
                  </div>
                ) : (
                  <React.Fragment key={`top-photo-${i}`}></React.Fragment>
                );
              })}
            </div>
            {!isEmbedded && wistiaVideoId && (
              <div className={styles.middle}>
                <WistiaPanel videoId={wistiaVideoId} responsivePadding={{ padding: '56.25% 0 0 0' }} />
              </div>
            )}
            <div className={styles.bottom}>
              {bottomPhotos.map((p, i) => {
                const hasEvenNumber = bottomPhotos.length % 2 === 0;
                return hasEvenNumber || bottomPhotos.length === 1 || i < bottomPhotos.length - 1 ? (
                  <div key={`bottom-photo-${i}`} className={styles.imageBox}>
                    <ImageLoader style={imageStyle} className={styles.grid} key={p.src} alt={`${product.title} ${i}`} src={sizeImage(p.src, '800x')} />
                  </div>
                ) : (
                  <React.Fragment key={`bottom-photo-${i}`}></React.Fragment>
                );
              })}
            </div>
            {possibleModels.length >= 2 && (
              <SlideToggle
                selectedOption={activeModel}
                option1={{
                  label: `${possibleModels[0].modelName} - ${possibleModels[0].size}`,
                  value: possibleModels[0].modelName,
                  onClickCallback: () => setActiveModel(possibleModels[0].modelName),
                }}
                option2={{
                  label: `${possibleModels[1].modelName} - ${possibleModels[1].size}`,
                  value: possibleModels[1].modelName,
                  onClickCallback: () => setActiveModel(possibleModels[1].modelName),
                }}
                style={{ marginTop: 8 }}
              />
            )}
          </div>
          <div className={styles.rightWrapper}>
            {breadCrumbs && <BreadCrumbs items={breadCrumbs} style={{ margin: '0 0 5px 0', borderBottom: 'none' }} />}
            <ProductPriceSummary product={product} variant={variant} reviewsSectionRef={reviewsRef} color={color} />
            <ProductPerks product={product} />
            <div ref={controlsTopRef} style={{ height: 1 }} />
            <ProductControls selectionType={SelectionType.ProductSection} selectionKey="0" style={productControlsStyle} />
            <GuaranteePanel style={{ marginTop: 10 }} image={isNoReturns ? undefined : assetPathFn('30-day.png')} guarantees={guarantees} />
            <CollapsePanels product={product} />
            <CompleteTheLook style={{ marginTop: 20 }} product={product} selectionType={SelectionType.ProductSection} />
          </div>
        </div>
      </CardBody>
      <CardFooter />
      {!isGiftCard && (
        <>
          <CardHeader>
            <CardTitle text="WHAT OUR CUSTOMERS ARE SAYING" />
          </CardHeader>
          <ProductReviewsDesktop ref={reviewsRef} product={product} />
        </>
      )}
      <CardHeader>
        <CardTitle text="Featured" />
      </CardHeader>
      <CollectionCards />
      <CardHeader>
        <WorldsBestTitle />
      </CardHeader>
      <CardBody style={{ padding: '40px 60px 50px 60px', paddingTop: 40, boxSizing: 'border-box' }}>
        <WorldsBestShapingPanel />
      </CardBody>
      <CardFooter />
      <CardHeader>
        <CardTitle text="Discover Honeylove" />
      </CardHeader>
      <CardBody>
        <DiscoverHoneylove />
      </CardBody>
    </>
  );
};

export default ProductDetailPageDesktop;
